import { useId, useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { CalendarClock } from 'lucide-react';
import { ADULT_AGE_YEARS, ageInYears, type CountryProfile, type RegisterPatientInput } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, todayIso } from '@/lib/utils';
import { Badge, DatePicker, Field, Input } from '@/components/ui/primitives';
import { HijriDobField } from './IdentityDocumentFields';
import { ageSummary } from './registration-rules';
import { ageSummaryText } from './patient-labels';

const OLDEST_YEARS = 130;

function earliestBirthDate(): string {
  const today = new Date();
  return `${today.getUTCFullYear() - OLDEST_YEARS}-01-01`;
}

export function DobAgeField({
  form,
  profile,
  required,
  onDobChange,
  className,
}: {
  form: UseFormReturn<RegisterPatientInput>;
  profile: CountryProfile;
  required?: boolean;
  onDobChange?: () => void;
  className?: string;
}) {
  const dobId = useId();
  const [hijri, setHijri] = useState(false);
  const dob = useWatch({ control: form.control, name: 'dob' });
  const approximate = useWatch({ control: form.control, name: 'approximateAgeYears' });
  const knownApproximate = typeof approximate === 'number' && !Number.isNaN(approximate) ? approximate : null;
  const derived = dob ? ageInYears(dob, null) : null;
  const ageText = dob ? (derived === null || derived < 1 ? '' : String(derived)) : knownApproximate === null ? '' : String(knownApproximate);
  const summary = ageSummary(dob, knownApproximate);
  const years = ageInYears(dob || null, knownApproximate);
  const errors = form.formState.errors;
  const offersHijri = profile.identity.dobCalendars.includes('islamic-umalqura');

  const setDob = (value: string) => {
    form.setValue('dob', value || undefined, { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
    if (value) form.setValue('approximateAgeYears', undefined, { shouldDirty: true });
    onDobChange?.();
  };

  const setAge = (raw: string) => {
    const parsed = raw.trim() === '' ? undefined : Math.max(0, Math.min(OLDEST_YEARS, Math.trunc(Number(raw))));
    form.setValue('dob', undefined, { shouldDirty: true });
    form.setValue('approximateAgeYears', parsed === undefined || Number.isNaN(parsed) ? undefined : parsed, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
  };

  const error = errors.dob?.message ?? errors.approximateAgeYears?.message;

  return (
    <Field
      label={hijri ? tr('opd', 'date_of_birth_hijri') : tr('opd', 'date_of_birth')}
      htmlFor={hijri ? undefined : dobId}
      required={required}
      error={error}
      hint={tr('opd', 'dob_or_age_hint')}
      className={className}
    >
      <input type="hidden" data-field-value value={dob || ageText} />
      <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
        <div className="min-w-[10rem] flex-1">
          {hijri ? (
            <HijriDobField value={dob ?? undefined} onChange={setDob} />
          ) : (
            <DatePicker
              id={dobId}
              value={dob ?? ''}
              min={earliestBirthDate()}
              max={todayIso()}
              onChange={setDob}
              onUnreadable={(raw) => {
                if (!/^\d{1,3}$/.test(raw) || Number(raw) > OLDEST_YEARS) return false;
                setAge(raw);
                return true;
              }}
              aria-invalid={Boolean(errors.dob) || undefined}
            />
          )}
        </div>
        <span className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{tr('opd', 'or')}</span>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min={0}
            max={OLDEST_YEARS}
            inputMode="numeric"
            aria-label={tr('opd', 'age_in_years')}
            className="w-24"
            value={ageText}
            onChange={(event) => setAge(event.target.value)}
          />
          <span className="text-xs text-muted-foreground">{tr('opd', 'years_short')}</span>
        </div>
      </div>
      <div className="flex min-h-[1.25rem] flex-wrap items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
        {summary ? (
          <>
            <CalendarClock className="h-3.5 w-3.5" aria-hidden />
            <span className={cn('font-medium', summary.estimated ? 'text-muted-foreground' : 'text-foreground')}>{ageSummaryText(summary)}</span>
            {summary.estimated && <Badge variant="muted">{tr('opd', 'estimated')}</Badge>}
            {years !== null && years < 1 && <Badge variant="info">{tr('opd', 'infant')}</Badge>}
            {years !== null && years >= 1 && years < ADULT_AGE_YEARS && <Badge variant="warning">{tr('opd', 'minor')}</Badge>}
          </>
        ) : (
          <span>{tr('opd', 'dob_or_age_empty')}</span>
        )}
        {offersHijri && (
          <button type="button" className="ms-auto rounded text-xs font-medium text-primary underline-offset-2 hover:underline focus-ring" onClick={() => setHijri(!hijri)}>
            {hijri ? tr('opd', 'enter_the_gregorian_date_instead') : tr('opd', 'enter_the_date_in_hijri')}
          </button>
        )}
      </div>
    </Field>
  );
}
